import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftSFill, RiArrowRightSFill } from 'react-icons/ri';

import { Button } from '../button';

interface PaginationProps {
  className?: string;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  setPage: (page: number) => void;
  visible?: boolean;
}

export const Pagination = ({
  className,
  totalCount,
  currentPage,
  setPage,
  pageSize,
  visible = false,
}: PaginationProps) => {
  const { t } = useTranslation('common');

  const first = useMemo(() => (totalCount <= 0 ? 0 : currentPage * pageSize + 1), [currentPage, pageSize, totalCount]);
  const last = useMemo(
    () => (totalCount <= 0 ? 0 : Math.min(first + pageSize - 1, totalCount)),
    [first, pageSize, totalCount],
  );

  const prevDisabled = useMemo(() => currentPage === 0, [currentPage]);
  const nextDisabled = useMemo(() => last >= totalCount, [last, totalCount]);

  if (!visible && totalCount <= pageSize) return null;

  return (
    <div className={classNames('flex justify-between px-4 py-4', className)}>
      <span className="font-medium self-center text-sm text-white">
        {t('pagination.message', { first, last, total: totalCount })}
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={prevDisabled} color="primary-dark" onClick={() => setPage(currentPage - 1)}>
          <RiArrowLeftSFill />
          {t('pagination.previous')}
        </Button>
        <Button size="sm" disabled={nextDisabled} color="primary-dark" onClick={() => setPage(currentPage + 1)}>
          {t('pagination.next')}
          <RiArrowRightSFill />
        </Button>
      </div>
    </div>
  );
};
